/**
 * Dub.co referral click capture.
 *
 * A vendor's Dub short link (e.g. https://refer.davidsbridal.com/abc) records
 * the click and redirects here with `?dub_id=<clickId>`. We persist that id in
 * a first-party `dub_id` cookie -- the same name the Dub script uses -- so it
 * survives navigation through the signup flow and is still available even
 * when an ad blocker stops the Dub script from loading. At signup the id is
 * sent to the Pearl API as `dubClickId`.
 *
 * Like utm.js, nothing here ever throws: attribution is not worth breaking
 * app load or signup over.
 */

const DUB_CLICK_ID_PARAM = 'dub_id';
const DUB_COOKIE_NAME = 'dub_id';
const DUB_COOKIE_MAX_AGE_DAYS = 90;
const SECONDS_PER_DAY = 24 * 60 * 60;

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

/**
 * The cookie Domain attribute to use, or undefined to write a host-only cookie.
 *
 * Omitted on localhost, and whenever the configured domain does not cover the
 * current host -- the browser would silently reject the cookie otherwise.
 */
const resolveCookieDomain = (hostname, configuredDomain) => {
    if (!hostname || LOCAL_HOSTNAMES.includes(hostname)) return undefined;
    const domain = (configuredDomain || '').trim();
    if (!domain) return undefined;

    const bare = domain.replace(/^\./, '').toLowerCase();
    const host = hostname.toLowerCase();
    if (host !== bare && !host.endsWith(`.${bare}`)) return undefined;

    return domain;
};

/**
 * Read the Dub click id from a query string.
 *
 * @param {string} search a location.search value ('?a=b&dub_id=...')
 * @returns {string} the click id, or '' when absent
 */
const getDubClickIdFromSearch = (search) => {
    if (typeof search !== 'string' || search === '') return '';
    try {
        const value = new URLSearchParams(search).get(DUB_CLICK_ID_PARAM);
        return value ? value.trim() : '';
    } catch {
        return '';
    }
};

/**
 * Build the Set-Cookie string for a click id.
 *
 * @param {string} clickId
 * @param {object} options { hostname, protocol, cookieDomain }
 */
const buildDubCookie = (clickId, { hostname, protocol, cookieDomain } = {}) => {
    const parts = [
        `${DUB_COOKIE_NAME}=${encodeURIComponent(clickId)}`,
        'path=/',
        `max-age=${DUB_COOKIE_MAX_AGE_DAYS * SECONDS_PER_DAY}`,
        'SameSite=Lax',
    ];
    const domain = resolveCookieDomain(hostname, cookieDomain);
    if (domain) parts.push(`domain=${domain}`);
    if (protocol === 'https:') parts.push('Secure');
    return parts.join('; ');
};

/**
 * On app load: if the URL carries `dub_id`, persist it as a first-party cookie.
 * A new click id replaces an older one (last click wins, matching Dub's
 * default attribution model).
 *
 * @returns {string} the captured click id, or '' when there was none
 */
const captureDubClickId = () => {
    try {
        if (typeof window === 'undefined' || typeof document === 'undefined') return '';
        const { search, hostname, protocol } = window.location || {};
        const clickId = getDubClickIdFromSearch(search);
        if (!clickId) return '';

        document.cookie = buildDubCookie(clickId, {
            hostname,
            protocol,
            cookieDomain: process.env.REACT_APP_DUB_COOKIE_DOMAIN,
        });
        return clickId;
    } catch {
        return '';
    }
};

/**
 * The persisted Dub click id, whether written by captureDubClickId or by the
 * Dub script itself.
 *
 * @returns {string} the click id, or '' when there is none
 */
const getDubClickId = () => {
    try {
        if (typeof document === 'undefined' || !document.cookie) return '';
        const prefix = `${DUB_COOKIE_NAME}=`;
        const match = document.cookie
            .split(';')
            .map((c) => c.trim())
            .find((c) => c.startsWith(prefix));
        if (!match) return '';

        const raw = match.slice(prefix.length);
        try {
            return decodeURIComponent(raw).trim();
        } catch {
            return raw.trim();
        }
    } catch {
        return '';
    }
};

/**
 * `{ dubClickId }` when a click id is stored, otherwise `{}` -- spread into a
 * request body so signup payloads are unchanged when there was no referral.
 */
const getDubClickIdPayload = () => {
    const dubClickId = getDubClickId();
    return dubClickId ? { dubClickId } : {};
};

export {
    DUB_COOKIE_NAME,
    buildDubCookie,
    captureDubClickId,
    getDubClickId,
    getDubClickIdFromSearch,
    getDubClickIdPayload,
    resolveCookieDomain,
};
